import Result from "@Core/Result";
import { ServiceBase } from "@Core/ServiceBase";
import { IAssessmentDetails, IAssessmentInstanceDetails } from "@Models/IAssessmentDetails";
import { IEntity } from "@Models/IEntity";
import { IFeedbackInstanceDetails } from "@Models/IFeedbackInstanceDetails";
import { IPdfItem } from "@Models/IPdfItem";
import { ITaskSubmissionDetails } from "@Models/ITaskDetails";

const API_URL = "/api/Assessment";

export default class AssessmentService extends ServiceBase {
    public async create(userId: string, model: IAssessmentDetails): Promise<Result<{ id: string }>> {
        return await this.requestJson({
            url: `${API_URL}/Create/${userId}`,
            method: "POST",
            data: model,
        });
    }

    /**
     * Create a metadata request for a given assessment
     */
    public async createMetadata(
        ownerId: string,
        assessmentId: string,
        assessmentInstanceId: string
    ): Promise<Result<IEntity>> {
        return await this.requestJson<IEntity>({
            url: `${API_URL}/CreateMetadata/${ownerId}/${assessmentId}?id=${assessmentInstanceId}`,
            method: "POST",
        });
    }

    public async completeInstance(assessmentId: string, model: IFeedbackInstanceDetails): Promise<Result<boolean>> {
        return await this.requestJson({
            url: `${API_URL}/Complete/${assessmentId}`,
            method: "PATCH",
            data: model,
        });
    }

    public async update(assessmentId: string, model: IAssessmentDetails): Promise<Result<boolean>> {
        return await this.requestJson({
            url: `${API_URL}/Update/${assessmentId}`,
            method: "PATCH",
            data: model,
        });
    }

    public async getInstances(
        assessmentId: string
    ): Promise<Result<{ assessmentInstances: IAssessmentInstanceDetails[] }>> {
        return await this.requestJson({
            url: `${API_URL}/GetInstances/${assessmentId}`,
            method: "GET",
        });
    }

    public async getUserInstances(
        userId: string,
        assessmentInstanceId: string = null
    ): Promise<Result<IAssessmentInstanceDetails[]>> {
        return await this.requestJson({
            url: `${API_URL}/GetUserInstances/${userId}${!!assessmentInstanceId ? `?id=${assessmentInstanceId}` : ""}`,
            method: "GET",
        });
    }

    /**
     * Get an assessment instance report based on the metadata.
     * @param metadataId
     * @param userId
     */
    public async getReport(userId: string, assessmentId: string, metadataId: string): Promise<Result<IPdfItem[]>> {
        return await this.requestJson<IPdfItem[]>({
            url: `${API_URL}/GetReport/${userId}/${assessmentId}?metadata-id=${metadataId}`,
            method: "GET",
        });
    }

    public async createInstance(assessmentId: string, userId: string): Promise<Result<boolean>> {
        return await this.requestJson({
            url: `${API_URL}/CreateInstance/${assessmentId}/${userId}`,
            method: "POST",
        });
    }

    public async delete(assessmentId: string): Promise<Result<boolean>> {
        return await this.requestJson({
            url: `${API_URL}/Delete/${assessmentId}`,
            method: "DELETE",
        });
    }

    public async submit(
        userId: string,
        assessmentId: string,
        model: ITaskSubmissionDetails[]
    ): Promise<Result<boolean>> {
        return await this.requestJson({
            url: `${API_URL}/Submit/${userId}/${assessmentId}`,
            method: "PATCH",
            data: model,
        });
    }

    public async deleteInstance(userId: string, assessmentId: string): Promise<Result<boolean>> {
        return await this.requestJson({
            url: `${API_URL}/DeleteInstance/${userId}/${assessmentId}`,
            method: "DELETE",
        });
    }

    public async getAll(userId: string): Promise<Result<{ assessments: IAssessmentDetails[] }>> {
        return await this.requestJson({
            url: `${API_URL}/GetAll/${userId}`,
            method: "GET",
        });
    }
}